/**
 * _support_inbox.js — pure helpers of the staff support inbox
 * (support_inbox_controller.js), tested in tests/js/support_inbox.test.mjs.
 *
 * The rows are the server's thread rows (SupportService.thread_row); live
 * events update a copy of them between fetches, and rowFromThread turns one
 * into what a list row shows. An event for a thread not in the list returns
 * null: the caller refetches rather than guess where it belongs.
 */
import { previewText } from "./_support_alerts.js";

const WAITING_VIEWS = ['waiting', 'unclaimed'];

/** How long a customer has waited: '12 min', '3 h', '2 d', or '' when nobody waits. */
export function waitingLabel(waitingSince, now = new Date()) {
    if (!waitingSince) return '';
    const minutes = Math.floor((now - new Date(waitingSince)) / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} min`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)} h`;
    return `${Math.floor(minutes / 1440)} d`;
}

function claimLabel(thread, meId) {
    if (!thread.conversation_id) return 'New conversation';
    if (thread.status === 'resolved') return 'Resolved';
    if (!thread.claimed_by_user_id) return 'Unclaimed';
    if (thread.claimed_by_user_id === meId) return 'Yours';
    return thread.claimed_by_first_name || 'Claimed';
}

function lastPreview(message) {
    if (!message) return '';
    return message.kind === 'system' ? message.body || '' : previewText(message);
}

/** What a list row shows for one server thread row. */
export function rowFromThread(thread, meId, now = new Date()) {
    return {
        id: thread.conversation_id,
        name: thread.customer_name,
        business: thread.business_name || '',
        preview: lastPreview(thread.last_message),
        waiting: waitingLabel(thread.waiting_since, now),
        claim: claimLabel(thread, meId),
        unread: thread.unread || 0,
    };
}

/**
 * A thread row for a conversation not started yet, from a customer search
 * result (GET /api/support/customers): what the staff header shows until the
 * first send creates the thread and the server's row replaces it.
 */
export function draftThread(customer) {
    return {
        conversation_id: null, customer_user_id: customer.user_id,
        customer_name: customer.name, customer_email: customer.email, business_name: null,
        status: 'open', claimed_by_user_id: null, claimed_by_first_name: null,
        waiting_since: null, last_message: null, unread: 0,
    };
}

const THREAD_CHANGES = {
    claimed: (data) => ({ claimed_by_user_id: data.claimed_by_user_id,
                          claimed_by_first_name: data.claimed_by_first_name }),
    released: () => ({ claimed_by_user_id: null, claimed_by_first_name: null }),
    resolved: () => ({ status: 'resolved', waiting_since: null,
                       claimed_by_user_id: null, claimed_by_first_name: null }),
    reopened: () => ({ status: 'open' }),
};

function messageChange(thread, message) {
    const fromCustomer = message.sender_user_id !== null && !message.is_staff_sender;
    return { last_message: message, unread: (thread.unread || 0) + (fromCustomer ? 1 : 0) };
}

/** Rows after a live event (new array, inputs untouched), or null to refetch. */
export function applyThreadEvent(threads, { type, data }) {
    const index = threads.findIndex((t) => t.conversation_id === data.conversation_id);
    if (index === -1) return null;
    const thread = threads[index];
    if (type === 'support_message') {
        const moved = { ...thread, ...messageChange(thread, data) };
        return [moved, ...threads.filter((_t, i) => i !== index)];
    }
    const change = THREAD_CHANGES[data.change];
    if (type !== 'support_thread' || !change) return threads;
    return threads.map((t, i) => (i === index ? { ...t, ...change(data) } : t));
}

function lastId(thread) {
    return thread.last_message?.id || 0;
}

/** The order a filter shows: the longest wait first, otherwise the newest message first. */
export function sortRows(threads, filter) {
    const rows = [...threads];
    if (WAITING_VIEWS.includes(filter)) {
        const since = (t) => (t.waiting_since ? new Date(t.waiting_since).getTime() : Infinity);
        return rows.sort((a, b) => since(a) - since(b));
    }
    return rows.sort((a, b) => lastId(b) - lastId(a));
}
